<template>
  <div>
    <div>
      <v-breadcrumbs
        class="px-0"
        :items="[
          { title: '成長記録一覧', href: '/growth-record', color: 'primary' },
          { title: `${childName} の成長記録` },
        ]"
      ></v-breadcrumbs>
    </div>
    <page-title :value="`${childName} の成長記録`" />
    <div class="my-4" style="width: 200px;">
      <v-text-field 
        variant="outlined"
        density="compact"
        v-model.number="year" suffix="年度"
        type="number"
      />
    </div>

    <!-- 異常値メッセージ表示（画面上での注意喚起） -->
    <v-alert v-if="hasAbnormalValue" type="error" density="compact" class="mb-3" title="入力値にエラーがあります">
      入力された数値が大きすぎます。小数点（.）の打ち忘れを確認してください
    </v-alert>

    <div v-if="recordRequests.length > 0" class="py-2">
      <!-- 異常値がある場合はボタンを disabled にする -->
      <v-btn
        color="primary"
        variant="flat"
        :disabled="isSaving || hasAbnormalValue"
        @click="saveRecords"
      >
        保存する
      </v-btn>
    </div>
    <div v-if="isSaving" class="my-2">
      <v-progress-linear indeterminate color="primary" />
    </div>
    
    <div class="children-table">
      <div class="children-table__headers">
        <div class="children-table__header col-month"></div>
        <div class="children-table__header col-height">身長 (cm)</div>
        <div class="children-table__header col-weight">体重 (kg)</div>
        <div class="children-table__header col-other">備考</div>
      </div>
      <child-growth-record-row
        v-for="r in recordsWithMonth"
        :key="r.month"
        :record="r.record"
        :month="r.month"
        @inputHeight="inputHeight($event, r)"
        @inputWeight="inputWeight($event, r)"
        @inputRemarks="inputRemarks($event, r)"
      />
    </div>
    <div v-if="growthRecord && growthRecord.records.length > 0">
      <div>
        <g-chart
          type="ComboChart"
          :data="heightChartData"
          :options="{
            colors:['transparent', colorChartArea, colorPrimary],
            seriesType: 'area',
            isStacked: true,
            series: {
              0: { lineWidth: 0, enableInteractivity: false, visibleInLegend: false },
              1: { lineWidth: 0, enableInteractivity: false, labelInLegend: 'パーセンタイル値 3% ~ 97%の範囲'},
              2: { lineWidth: 2, pointSize: 6, type: 'line' },
            },
            height: 700
          }"
        />
      </div>
      <div>
        <g-chart
          type="ComboChart"
          :data="weightChartData"
          :options="{
            colors:['transparent', colorChartArea, colorPrimary],
            seriesType: 'area',
            isStacked: true,
            series: {
              0: { lineWidth: 0, enableInteractivity: false, visibleInLegend: false },
              1: { lineWidth: 0, enableInteractivity: false, labelInLegend: 'パーセンタイル値 3% ~ 97%の範囲'},
              2: { lineWidth: 2, pointSize: 6, type: 'line' },
            },
            height: 700
          }"
        />
      </div>
    </div>
    <div v-else>
      <v-card
        variant="outlined"
        class="no-chart-card"
      >
        成長記録のデータを入力すると、グラフが表示されます。
      </v-card>
    </div>

    <!-- 異常値保存ブロック用ダイアログ -->
    <v-dialog v-model="showErrorAlert" max-width="450">
      <v-card>
        <v-card-title class="text-h6 text-error font-weight-bold">
          ⚠️ 保存できません
        </v-card-title>
        <v-card-text>
          入力値に異常（許容範囲外の数値）が含まれているため、保存できません。<br />
          正しい数値を入力し直してください。
          <ul class="mt-3 text-caption text-error">
            <li>身長の許容範囲: 30cm 〜 200cm</li>
            <li>体重の許容範囲: 1kg 〜 100kg</li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="flat" @click="showErrorAlert = false">
            閉じる
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import PageTitle from '@/components/atoms/PageTitle.vue';
import ChildGrowthRecordRow from '@/components/organisms/growth-record/ChildGrowthRecordRow.vue';
import dayjs from 'dayjs';
import { GChart } from 'vue-google-charts'

import { childGrowthRecordsModule, GrowthRepuqestWithMonth } from '@/store/viewModules/growth-record/childGrowthRecordsModule';
import { NurseryGrowthResponse, NurseryGrowthDetailResponse } from 'chaild-api/lib';

interface RecordsWithMonth {
  month: number;
  record: NurseryGrowthResponse | null;
}

interface DataType {
  isSaving: boolean;
  showErrorAlert: boolean;
  colorPrimary: string;
  colorChartGray: string;
  colorChartArea: string;
}

export default defineComponent({
  components: {
    PageTitle,
    ChildGrowthRecordRow,
    GChart,
  },

  data: (): DataType => ({
    isSaving: false,
    showErrorAlert: false,
    colorPrimary: '#029ec0',
    colorChartGray: '#ccc',
    colorChartArea: '#67c4d9'
  }),

  computed: {
    year: {
      get() {
        return childGrowthRecordsModule.yearInt;
      },
      set(value: number) {
        if (value > 2000 && value < 2100) {
          childGrowthRecordsModule.setYear(`${value}`)
          this.fetchGrowthRecord()
        }
      }
    },
    growthRecord() {
      return childGrowthRecordsModule.growthRecord;
    },
    recordsWithMonth(): RecordsWithMonth[] {
      const numbers = [...Array(12)].map((emp, i) => (i + 4) % 12);
      return numbers.map((m) => {
        const month = m === 0 ? 12 : m;
        if (this.growthRecord) {
          const find = this.growthRecord.records.find((r) => r.month === month);
          return {
            month,
            record: find || null,
          }
        }
        return {
          month,
          record: null,
        }
      });
    },
    childName(): string {
      if (this.growthRecord) {
        return `${this.growthRecord.child.lastName} ${this.growthRecord.child.firstName}`;
      }
      return '読み込み中...';
    },
    recordRequests() {
      return childGrowthRecordsModule.childGrowthRecordRequests;
    },
    // 異常値チェック判定プロパティ
    hasAbnormalValue(): boolean {
      return this.recordRequests.some((item) => {
        const req = item.request;
        if (!req) return false;

        // 身長チェック（数値変換して判定）
        if (req.height !== undefined && req.height !== null) {
          const height = Number(req.height);
          if (!Number.isNaN(height) && String(req.height).trim() !== '') {
            if (height > 130) {
              return true;
            }
          }
        }

        // 体重チェック（数値変換して判定）
        if (req.weight !== undefined && req.weight !== null) {
          const weight = Number(req.weight);
          if (!Number.isNaN(weight) && String(req.weight).trim() !== '') {
            if (weight > 50) {
              return true;
            }
          }
        }

        return false;
      });
    },
    heightChartData() {
      if (!this.growthRecord) {
        return [];
      }

      const growthRecord = {...this.growthRecord};
      const numbers = [...Array(12)].map((emp, i) => (i + 4) % 12);
      const chartData: Array<Array<string | number | null>> = [['月', 'パーセンタイル値 3%', 'パーセンタイル値 97%', '身長']];
      
      numbers.map((m) => {
        const month = m === 0 ? 12 : m;
        const col: Array<string | number | null> = [`${month}月`]
        const find = growthRecord.records.find((r) => r.month === month);
        if (find) {
          const year = month < 4 ? (this.year) + 1 : (this.year);
          const percenTiles = this.getHeightPercentile(growthRecord, year, month);
          if (percenTiles['3'] && percenTiles['97']) {
            col.push(percenTiles['3'])
            col.push(percenTiles['97'] - percenTiles['3'])
          } else {
            col.push(null)
            col.push(null)
          }

          const value = find ? find.height : null;
          col.push(value);
          chartData.push(col);
        } else {
          col.push(null)
          col.push(null)
          col.push(null)
          chartData.push(col);
        }
      });

      return chartData;
    },
    weightChartData() {
      if (!this.growthRecord) {
        return [];
      }

      const growthRecord = {...this.growthRecord};
      const numbers = [...Array(12)].map((emp, i) => (i + 4) % 12);
      const chartData: Array<Array<string | number | null>> = [['月', 'パーセンタイル値 3%', 'パーセンタイル値 97%', '体重']];
      
      numbers.map((m) => {
        const month = m === 0 ? 12 : m;
        const col: Array<string | number | null> = [`${month}月`]
        const find = growthRecord.records.find((r) => r.month === month);
        if (find) {
          const year = month < 4 ? (this.year) + 1 : (this.year);
          const percenTiles = this.getWeightPercentile(growthRecord, year, month);
          if (percenTiles['3'] && percenTiles['97']) {
            col.push(percenTiles['3'])
            col.push(percenTiles['97'] - percenTiles['3'])
          } else {
            col.push(null)
            col.push(null)
          }

          const value = find ? find.weight : null;
          col.push(value);
          chartData.push(col);
        } else {
          col.push(null)
          col.push(null)
          col.push(null)
          chartData.push(col);
        }
      });

      return chartData;
    }
  },

  methods: {
    async fetchGrowthRecord() {
      this.isSaving = true
      await childGrowthRecordsModule.getGrowthRecord()
      this.isSaving = false
    },
    inputHeight(value: number, record: RecordsWithMonth) {
      if (this.growthRecord) {
        const find = this.recordRequests.find((r) => r.month === record.month);
        const newRecord: GrowthRepuqestWithMonth = find ? {...find} : { 
          month: record.month,
          request: {
            childId: this.growthRecord.child.childId,
          }
        };
        newRecord.request.height = value;
        childGrowthRecordsModule.updateChildGrowthRecordRequests(newRecord);
      }
    },
    inputWeight(value: number, record: RecordsWithMonth) {
      if (this.growthRecord) {
        const find = this.recordRequests.find((r) => r.month === record.month);
        const newRecord: GrowthRepuqestWithMonth = find ? {...find} : { 
          month: record.month,
          request: {
            childId: this.growthRecord.child.childId,
          }
        };
        newRecord.request.weight = value;
        childGrowthRecordsModule.updateChildGrowthRecordRequests(newRecord);
      }
    },
    inputRemarks(value: string, record: RecordsWithMonth) {
      if (this.growthRecord) {
        const find = this.recordRequests.find((r) => r.month === record.month);
        const newRecord: GrowthRepuqestWithMonth = find ? {...find} : { 
          month: record.month,
          request: {
            childId: this.growthRecord.child.childId,
          }
        };
        newRecord.request.remarks = value;
        childGrowthRecordsModule.updateChildGrowthRecordRequests(newRecord);
      }
    },
    async saveRecords() {
      // 異常値が存在する場合は保存を行わずにダイアログを出してブロック
      if (this.hasAbnormalValue) {
        this.showErrorAlert = true;
        return;
      }

      this.isSaving = true;
      try {
        await childGrowthRecordsModule.saveRecords();
        await childGrowthRecordsModule.getGrowthRecord();
      } finally {
        this.isSaving = false;
      }
    },
    getHeightPercentile(record: NurseryGrowthDetailResponse | null, year: number, month: number): { [key: number]: number | null } {
      if (record) {
        const birthDay = dayjs(record.child.dateOfBirth);
        const months = dayjs(`${year}-${month}`).diff(birthDay, 'month');
        const gender = record.child.gender;
        const percentileValues = childGrowthRecordsModule.percentileMaster.filter((pm) => {
          if (
            pm.sex === gender
            && pm.month === months
            && pm.valueType === 'height'
          ) {
            return true;
          }
          return false;
        })
        if (percentileValues.length === 7) {
          percentileValues.sort((pv1, pv2) => {
            return pv1.percentile - pv2.percentile
          })
          return {
            3: percentileValues[0].value,
            10: percentileValues[1].value,
            25: percentileValues[2].value,
            50: percentileValues[3].value,
            75: percentileValues[4].value,
            90: percentileValues[5].value,
            97: percentileValues[6].value,
          }
        }
      }
      return { 3: null, 10: null, 25: null, 50: null, 75: null, 90: null, 97: null }
    },
    getWeightPercentile(record: NurseryGrowthDetailResponse | null, year: number, month: number): { [key: number]: number | null } {
      if (record) {
        const birthDay = dayjs(record.child.dateOfBirth);
        const months = dayjs(`${year}-${month}`).diff(birthDay, 'month');
        const gender = record.child.gender;
        const percentileValues = childGrowthRecordsModule.percentileMaster.filter((pm) => {
          if (
            pm.sex === gender
            && pm.month === months
            && pm.valueType === 'weight'
          ) {
            return true;
          }
          return false;
        })
        if (percentileValues.length === 7) {
          percentileValues.sort((pv1, pv2) => {
            return pv1.percentile - pv2.percentile
          })
          return {
            3: percentileValues[0].value,
            10: percentileValues[1].value,
            25: percentileValues[2].value,
            50: percentileValues[3].value,
            75: percentileValues[4].value,
            90: percentileValues[5].value,
            97: percentileValues[6].value,
          }
        }
      }
      return { 3: null, 10: null, 25: null, 50: null, 75: null, 90: null, 97: null }
    },
  },

  mounted() {
    const childId = this.$route.params.childId;
    if (childId) {
      try {
        const childIdInt = parseInt(childId as string, 10);
        const yearParam = this.$route.query.year;
        if (yearParam) {
          childGrowthRecordsModule.setYear(String(yearParam));
        } else {
          childGrowthRecordsModule.initYear();
        }
        childGrowthRecordsModule.setChildId(childIdInt);
        childGrowthRecordsModule.getGrowthRecord();
        childGrowthRecordsModule.getPercentile();
      } catch {
        return;
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.children-table__headers {
  display: flex;
  align-items: center;
  height: 30px;
  padding: 0 8px;
  border-bottom: solid 1px #e0e0e0;
  margin-bottom: 8px;
}

.children-table__header {
  font-size: 12px;
  font-weight: bold;
}

::v-deep {
  .children-table__row {
    display: flex;
    align-items: center;
    height: 48px;
    padding: 8px 0;
  }
  .children-table__row-col {
    padding: 4px;
    font-size: 14px;
  }
  .col-month { width: 80px; }
  .col-height { width: 120px; }
  .col-weight { width: 120px; }
  .col-other { flex: 1; }
}

.no-chart-card {
  padding: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 40px 0;
  background-color: #f5f5f5;
}
</style>